/**
 * SMS 재인증 신호 — 어디서 막히든 **한 곳으로** 모은다.
 *
 * ━━━ 왜 필요한가 (2026-09-22 · Codex 심사 [C-9]) ━━━
 *
 * 2단계 계획 1판은 거절 **계약**만 설계하고 **그걸 받는 화면**을 안 만들었다.
 *
 *   `SMSVerification` 은 로그인·가입 화면에만 붙어 있다.
 *   업무 화면에서 재인증이 필요해지면 **SMS 를 할 곳이 없다.**
 *   → 토큰은 지켜지는데 아무것도 안 되는 상태. 「부당한 로그아웃」보다 나쁘다.
 *
 * 그래서 재발급이 `sms_required` 를 받으면 여기로 신호를 보내고,
 * 앱 최상단에 사는 전역 창이 그것을 받아 뜬다.
 *
 * ━━━ 탭이 여러 개일 때 (Codex 심사 [C-5]) ━━━
 *
 * 탭 A 가 SMS 를 마치면 탭 B 의 열린 창은 **닫혀야 한다.** 낡은 챌린지로는 어차피 안 된다.
 * 토큰 저장은 이미 탭 간에 전파되므로(storage 이벤트) 그 신호를 여기서 받아 창을 닫는다.
 *
 * ⛔ 챌린지 **이중 소비 자체**는 서버가 막는다(`challenge_jti` + `consumed_at IS NULL`).
 *    여기서 막으려는 것은 「한쪽이 성공했는데 다른 쪽이 멈춰 있는 것」이다.
 */

export type SmsReauthRequest = {
  /** 서버가 내준 서명 챌린지. 이게 없으면 창을 띄울 수 없다. */
  challenge: string;
  /** 인증번호를 받을 번호. 서버 저장값(canonical)이다. */
  phoneNumber: string | null;
};

type 구독자 = (req: SmsReauthRequest | null) => void;

const 구독자들 = new Set<구독자>();

/** 지금 재인증이 필요한 상태인가. 창이 없을 때 온 신호도 잃지 않도록 붙들어 둔다. */
let 대기중: SmsReauthRequest | null = null;

function 알린다(): void {
  구독자들.forEach((fn) => {
    try {
      fn(대기중);
    } catch {
      /* 구독자 하나가 터져도 나머지는 알려야 한다 */
    }
  });
}

/**
 * 챌린지의 만료 시각(초)을 읽는다. **검증이 아니다** — 서명·만료는 서버가 본다.
 * 여기서는 「지금 들고 있는 것이 아직 쓸 수 있는가」만 판단한다.
 * 읽을 수 없으면 `null` 을 주고, 호출부는 그것을 «만료됨»으로 다루지 않는다
 * (읽기 실패를 이유로 사람이 쓰던 창을 갈아치우면 원래 결함이 되살아난다).
 */
function 만료시각(challenge: string): number | null {
  try {
    const payload = challenge.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = JSON.parse(json)?.exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

/** 지금 들고 있는 챌린지가 이미 죽었는가 (여유 10초). */
function 만료됐나(req: SmsReauthRequest): boolean {
  const exp = 만료시각(req.challenge);
  if (exp === null) return false;
  return exp * 1000 <= Date.now() + 10_000;
}

/**
 * 재인증이 필요하다고 알린다. 재발급 경로가 부른다.
 *
 * ⛔ 챌린지가 비어 있으면 **무시한다.** 빈 챌린지로 창을 띄우면 사람이 할 수 있는 게 없고,
 *    「인증하라는데 인증이 안 되는」 상태가 된다. 그럴 바엔 원래대로 실패시키는 게 낫다.
 */
export function requestSmsReauth(req: SmsReauthRequest): void {
  if (!req || !req.challenge) return;

  // ⛔ **이미 떠 있으면 새 챌린지로 바꾸지 않는다.** (2026-09-23 운영 실사용 결함)
  //
  //    종전에는 «같은 챌린지일 때만» 무시했다. 그런데 화면은 요청을 계속 보내고
  //    그때마다 갱신이 거절되면서 서버가 **매번 새 챌린지**를 내준다.
  //    챌린지가 바뀌면 `SMSVerification` 의 초기화 효과가 돈다
  //    (`smsChallenge` 가 의존성에 있다 — 전화번호를 바꿨을 때를 위한 것이라 그쪽은 옳다).
  //
  //    그래서 사람이 인증번호를 넣으려는 순간 **입력창이 닫히고 발송 버튼이 되살아났다.**
  //    영영 인증을 마칠 수 없다.
  //
  //    창이 떠 있는 동안 챌린지는 **고정**이어야 한다. 수명이 10분이라
  //    (`SMS_CHALLENGE_TTL_SEC`) 번호를 받아 넣기에 충분하다.
  //    창이 닫히면(인증 완료·로그아웃) 대기중이 비므로 다음 신호는 정상적으로 들어온다.
  //
  // ⛔ 단, **이미 죽은 챌린지는 붙들지 않는다.** 창을 10분 넘게 열어 둔 채 발송하면
  //    서버가 거절하는데, 그대로 고정하면 로그아웃 말고는 길이 없는 상태가 된다 —
  //    이 작업이 없애려던 바로 그 종류다. 만료된 경우에만 새것으로 갈아 끼운다.
  if (대기중 && !만료됐나(대기중)) return;

  대기중 = req;
  알린다();
}

/** 재인증이 끝났거나(성공) 그만두었다(취소). 창을 닫는다. */
export function clearSmsReauth(): void {
  if (!대기중) return;
  대기중 = null;
  알린다();
}

/** 지금 상태를 본다(구독 시작 시점에 이미 신호가 와 있을 수 있다). */
export function peekSmsReauth(): SmsReauthRequest | null {
  return 대기중;
}

/** 구독한다. 돌려주는 함수를 부르면 구독이 끊긴다. */
export function subscribeSmsReauth(fn: 구독자): () => void {
  구독자들.add(fn);
  // 붙는 즉시 현재 상태를 한 번 알린다 — 창이 늦게 마운트돼도 신호를 놓치지 않는다.
  try {
    fn(대기중);
  } catch {
    /* noop */
  }
  return () => {
    구독자들.delete(fn);
  };
}

/**
 * 다른 탭이 SMS 를 마쳤을 때 이 탭의 창을 닫는다.
 *
 * ⛔ 이 구독은 **앱이 뜰 때 한 번만** 건다. 창이 떠 있을 때만 걸면,
 *    창이 없는 탭은 다른 탭의 성공을 영영 모른다.
 */
export function watchOtherTabs(): () => void {
  if (typeof window === 'undefined') return () => {};

  const 핸들러 = (e: StorageEvent) => {
    // 토큰이 «새로 생긴» 경우만 본다. 지워진 경우(로그아웃)는 다른 길이 처리한다.
    if (e.key === 'auth_token' && e.newValue) clearSmsReauth();
  };

  window.addEventListener('storage', 핸들러);
  return () => window.removeEventListener('storage', 핸들러);
}
